import type { RuntimeCapabilityDefinition } from '../../catalog/index.ts';
import type { JsonObject } from '../../contracts/index.ts';
import type { RuntimeWorkerTransport } from '../RuntimeExecutionAdapter.ts';
import type { ExpressionOperatorRegistry } from './ExpressionOperatorRegistry.ts';
import { WorkerBackedRuntimeSession } from '../WorkerBackedRuntimeSession.ts';
import {
  DeclarativeModelRuntime,
  declarativeRuntimeConfigSchema,
} from './DeclarativeModelRuntime.ts';
import {
  createBuiltinExpressionOperatorRegistry,
} from './ExpressionOperatorRegistry.ts';

export function createDeclarativeModelRuntimeDefinition(
  customOperators?: ExpressionOperatorRegistry,
): RuntimeCapabilityDefinition {
  const operators = customOperators
    ?? createBuiltinExpressionOperatorRegistry();
  const workerCompatible = customOperators === undefined;
  return {
    descriptor: {
      key: 'probfun.declarative-model@1',
      title: '通用声明式模型',
      description: '执行受限 ModelSpec，驱动实体、状态、随机过程和观察数据。',
      capabilities: [
        'fixed-step',
        'discrete-event',
        'timers',
        'state-transitions',
        'entity-lifecycle',
        'safe-expressions',
        'seeded-random',
        'observation-output',
        'interaction-input',
        'playback-control',
        'parameter-update',
        'worker-execution',
      ],
      constraints: [
        '不执行 JavaScript、HTML 或 Vue 源码',
        '表达式仅允许白名单语法、函数和已声明符号',
        '不适用于高精度物理、流体或大型图计算',
      ],
      configSchemaId: 'probfun.declarative-model.config@1',
      configSchema: {
        type: 'object',
        required: ['model'],
        properties: {
          model: {
            $ref: 'probfun.model-spec@1',
            description: '受限声明式 ModelSpec',
          },
          parameters: {
            type: 'object',
            description: '覆盖 ModelSpec 中已声明的参数默认值',
            additionalProperties: true,
          },
        },
        additionalProperties: false,
      },
      metadata: {
        expressionOperators: operators.listDescriptors(),
      },
    },
    configSchema: declarativeRuntimeConfigSchema,
    validateConfig: config =>
      DeclarativeModelRuntime.validateConfiguration(config, operators),
    createSession: ({ declaration, observations, seed }) => {
      if (workerCompatible && supportsRuntimeWorker()) {
        try {
          const config = declarativeRuntimeConfigSchema.parse(
            declaration.config,
          );
          return new WorkerBackedRuntimeSession({
            id: declaration.id,
            capabilityKey: declaration.capabilityKey,
            config: declaration.config,
            seed,
            initialParameters: initialParameters(
              config.model,
              config.parameters,
            ),
            observations,
            worker: new Worker(
              new URL(
                '../workers/declarativeRuntime.worker.ts',
                import.meta.url,
              ),
              { type: 'module' },
            ) as unknown as RuntimeWorkerTransport,
          });
        }
        catch {
          // CSP、旧浏览器或 Worker 构造失败时保留可用的主线程降级。
        }
      }
      return new DeclarativeModelRuntime({
        id: declaration.id,
        config: declaration.config,
        observations,
        seed,
        operators,
      });
    },
  };
}

export const declarativeModelRuntimeDefinition
  = createDeclarativeModelRuntimeDefinition();

export * from './DeclarativeModelRuntime.ts';
export * from './ExpressionCapabilities.ts';
export * from './expressionLanguage.ts';
export * from './ExpressionOperatorRegistry.ts';
export * from './SafeExpressionEngine.ts';

function supportsRuntimeWorker(): boolean {
  return (
    typeof window !== 'undefined'
    && typeof Worker !== 'undefined'
  );
}

function initialParameters(
  model: import('../../contracts/index.ts').ModelSpec,
  overrides: JsonObject,
): JsonObject {
  return Object.fromEntries(model.parameters.map(parameter => [
    parameter.id,
    overrides[parameter.id] ?? parameter.default,
  ]));
}
